import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useDialog } from "@/context/DialogProvider";
import { Wrench, Plus, Trash2, Gauge, AlertCircle, BadgeCheck, Pencil, X as XIcon } from "lucide-react";
import StepLayout from "./StepLayout";
import { EQUIPMENT_TYPE_PRESETS } from "@/lib/equipmentTypes";

// Public presets — kept in /app/frontend/src/lib/equipmentTypes.js so the
// customer Equipment page can share the same list.
const DEFAULT_TYPES = EQUIPMENT_TYPE_PRESETS;

/**
 * Step 5 — Equipment + their CCPs.
 *
 * For each facility (within the active site) the owner adds equipment
 * pieces. Picking from a default type seeds the parameter / unit / safe
 * range, which we use to auto-create a CCP for that equipment so the
 * staff can immediately start logging readings.
 */
export default function Step5Equipment({ stepIndex, totalSteps, onAdvance, onBack, onFinishLater }) {
  const { notify } = useDialog();
  const [sites, setSites] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [ccps, setCcps] = useState([]);
  const [activeSite, setActiveSite] = useState("");
  const [activeFacility, setActiveFacility] = useState("");
  const [draft, setDraft] = useState(makeBlankDraft);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  function makeBlankDraft() {
    return {
      type_key: DEFAULT_TYPES[0].name,
      name: "",
      serial_number: "",
      location: "",
      monitoring_mode: "scheduled",
      // Readings per day — defaults to the preset's value. Persisted onto
      // the equipment-type when adding (PUT /equipment-types/{id}) so the
      // CCP-Data-Capture page renders the right number of slots per day.
      freq: DEFAULT_TYPES[0].freq,
      // Per-equipment override — defaults to the preset's value but the
      // owner can flip it on/off here for one-off cases.
      requires_calibration: DEFAULT_TYPES[0].requires_calibration,
      // Filled in when type_key === "__custom__"
      custom: {
        type_name: "",
        parameter: "Temperature",
        unit: "°C",
        min: 0,
        max: 5,
        freq: 2,
        desc: "",
        requires_calibration: false,
      },
    };
  }

  // Tab inside Step 5: "equipment" (main flow) or "calibrated" (calibrated
  // measurement gear like thermometers — calibration-only assets).
  const [tab, setTab] = useState("equipment");
  // Tenant-level default capture mode (set by the "we use our own loggers"
  // toggle). Pre-fills the per-equipment mode for new units.
  const [defaultMode, setDefaultMode] = useState("scheduled");
  // Calibrated-equipment draft — shape mirrors the existing
  // /calibrated-equipment endpoint on the customer Equipment page.
  const [calDraft, setCalDraft] = useState(makeBlankCalDraft);
  const [calibrated, setCalibrated] = useState([]);
  function makeBlankCalDraft() {
    return {
      facility_id: "",
      name: "",
      serial_number: "",
      model_number: "",
      last_calibrated_at: "",
      last_calibrated_by: "",
      calibration_interval_value: 12,
      calibration_interval_unit: "months",
      calibration_certificate_url: "",
    };
  }

  const load = async () => {
    try {
      const [{ data: s }, { data: f }, { data: e }, { data: t }, { data: c }, { data: cal }, prof] = await Promise.all([
        api.get("/sites"),
        api.get("/facilities"),
        api.get("/equipment"),
        api.get("/equipment-types"),
        api.get("/ccps"),
        api.get("/calibrated-equipment"),
        api.get("/tenant/profile").catch(() => ({ data: {} })),
      ]);
      setSites(s.sites || []);
      setFacilities(f.facilities || []);
      setEquipment(e.equipment || []);
      setEquipmentTypes(t.equipment_types || []);
      setCcps(c.ccps || []);
      // Backend returns `{ items }` (matches the main Equipment page).
      // The legacy `calibrated_equipment` key never existed — this used to
      // silently render an empty list even when inserts succeeded.
      setCalibrated(cal.items || []);
      const dm = prof.data?.profile?.default_monitoring_mode || "scheduled";
      setDefaultMode(dm);
      setDraft((d) => ({ ...d, monitoring_mode: d.monitoring_mode || dm }));
      if (!activeSite && s.sites?.length) setActiveSite(s.sites[0].id);
    } catch (ex) { console.debug("step5 load failed", ex); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Persist the tenant-level default capture mode. Loads the current profile
  // first so we don't wipe other fields (the profile PUT expects the full
  // object), then merges just the default_monitoring_mode.
  const setDefaultModePersist = async (mode) => {
    setDefaultMode(mode);
    setDraft((d) => ({ ...d, monitoring_mode: mode }));
    try {
      const { data } = await api.get("/tenant/profile");
      await api.put("/tenant/profile", {
        ...(data.profile || {}),
        default_monitoring_mode: mode,
      });
    } catch (ex) { console.debug("default mode persist failed", ex); }
  };

  // When site changes, default facility to first one in that site
  useEffect(() => {
    if (!activeSite) return;
    const fs = facilities.filter((f) => f.site_id === activeSite);
    if (fs.length > 0 && !fs.find((f) => f.id === activeFacility)) {
      setActiveFacility(fs[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSite, facilities.length]);

  // Equipment-type changes ripple into per-equipment calibration default
  // AND the readings-per-day input (so the user sees a sensible default
  // for the type they just picked).
  useEffect(() => {
    if (draft.type_key === "__custom__") return;
    const preset = DEFAULT_TYPES.find((t) => t.name === draft.type_key);
    if (!preset) return;
    setDraft((d) => ({
      ...d,
      requires_calibration: preset.requires_calibration,
      freq: preset.freq,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.type_key]);

  // Ensure an equipment_type exists in the tenant for the chosen default
  // OR for the user-defined custom entry. Created lazily on first use so
  // existing tenants aren't polluted with stuff they never touch.
  //
  // We ALSO upsert `collection_frequency_per_day` on every save. The freq
  // lives on the equipment-type (shared by all equipment of that type), so
  // updating it here means the CCP-Data-Capture page will show the right
  // number of reading slots per day for every fridge of this type going
  // forward. "Last write wins" — matches main-app behaviour.
  async function ensureEquipmentTypeId(typeKey, requiresCalibration) {
    if (typeKey === "__custom__") {
      const cust = draft.custom;
      if (!cust.type_name?.trim()) throw new Error("Custom type name is required");
      if (!cust.parameter?.trim()) throw new Error("Parameter name is required");
      if (!cust.unit?.trim()) throw new Error("Unit is required");
      const mn = parseFloat(cust.min), mx = parseFloat(cust.max);
      if (Number.isNaN(mn) || Number.isNaN(mx)) throw new Error("Min and max must be numbers");
      if (mn >= mx) throw new Error("Min must be less than max");
      const freq = Math.max(1, parseInt(cust.freq, 10) || 1);
      // Reuse an existing equipment-type with the same name if present
      const existing = equipmentTypes.find((et) => et.name === cust.type_name.trim());
      if (existing) {
        await api.put(`/equipment-types/${existing.id}`, {
          name: existing.name,
          description: existing.description || "",
          requires_calibration: !!requiresCalibration,
          default_calibration_interval_days: existing.default_calibration_interval_days || 365,
          calibration_interval_unit: existing.calibration_interval_unit || "days",
          has_operating_range: true,
          parameter_name: cust.parameter,
          parameter_unit: cust.unit,
          min_value: mn, max_value: mx,
          collection_frequency_per_day: freq,
        });
        return {
          typeRow: { ...existing, collection_frequency_per_day: freq },
          tmpl: { parameter: cust.parameter, unit: cust.unit, min: mn, max: mx, freq, desc: cust.desc || "" },
        };
      }
      const { data } = await api.post("/equipment-types", {
        name: cust.type_name.trim(),
        description: cust.desc || `${cust.parameter} ${mn}–${mx}${cust.unit}`,
        requires_calibration: !!requiresCalibration,
        default_calibration_interval_days: 365,
        calibration_interval_unit: "days",
        has_operating_range: true,
        parameter_name: cust.parameter,
        parameter_unit: cust.unit,
        min_value: mn,
        max_value: mx,
        collection_frequency_per_day: freq,
      });
      return {
        typeRow: data.equipment_type,
        tmpl: { parameter: cust.parameter, unit: cust.unit, min: mn, max: mx, freq, desc: cust.desc || "" },
      };
    }
    const tmpl = DEFAULT_TYPES.find((t) => t.name === typeKey);
    if (!tmpl) throw new Error("Unknown equipment type");
    const freq = Math.max(1, parseInt(draft.freq, 10) || tmpl.freq);
    const existing = equipmentTypes.find((et) => et.name === tmpl.name);
    if (existing) {
      // Update only when the user actually changed the freq from what's
      // already stored — avoids redundant PUTs.
      if ((existing.collection_frequency_per_day || 1) !== freq) {
        await api.put(`/equipment-types/${existing.id}`, {
          name: existing.name,
          description: existing.description || tmpl.desc,
          requires_calibration: !!requiresCalibration,
          default_calibration_interval_days: existing.default_calibration_interval_days || 365,
          calibration_interval_unit: existing.calibration_interval_unit || "days",
          has_operating_range: true,
          parameter_name: tmpl.parameter,
          parameter_unit: tmpl.unit,
          min_value: tmpl.min,
          max_value: tmpl.max,
          collection_frequency_per_day: freq,
        });
      }
      return { typeRow: { ...existing, collection_frequency_per_day: freq }, tmpl: { ...tmpl, freq } };
    }
    const { data } = await api.post("/equipment-types", {
      name: tmpl.name,
      description: tmpl.desc,
      requires_calibration: !!requiresCalibration,
      default_calibration_interval_days: 365,
      calibration_interval_unit: "days",
      has_operating_range: true,
      parameter_name: tmpl.parameter,
      parameter_unit: tmpl.unit,
      min_value: tmpl.min,
      max_value: tmpl.max,
      collection_frequency_per_day: freq,
    });
    return { typeRow: data.equipment_type, tmpl: { ...tmpl, freq } };
  }

  const addEquipment = async () => {
    setErr("");
    if (!activeFacility) return setErr("Pick a facility first.");
    if (!draft.name.trim()) return setErr("Equipment name is required.");
    if (!draft.serial_number.trim()) return setErr("Serial / asset number is required (identifies this unit on reports).");
    setBusy(true);
    try {
      const { typeRow, tmpl } = await ensureEquipmentTypeId(draft.type_key, draft.requires_calibration);
      // 1. Create the equipment row
      const { data: eq } = await api.post("/equipment", {
        site_id: activeSite,
        facility_id: activeFacility,
        equipment_type_id: typeRow.id,
        name: draft.name.trim(),
        serial_number: draft.serial_number.trim(),
        model_number: "",
        location: draft.location.trim(),
        monitoring_mode: draft.monitoring_mode || "scheduled",
      });
      // 2. Auto-create a CCP for this equipment using the type's safe range
      try {
        await api.post("/ccps", {
          site_id: activeSite,
          equipment_id: eq?.equipment?.id,
          name: `${draft.name.trim()} — ${tmpl.parameter}`,
          parameter: tmpl.parameter,
          unit: tmpl.unit,
          min_value: tmpl.min,
          max_value: tmpl.max,
          frequency_hours: tmpl.freq,
          description: tmpl.desc,
        });
      } catch (ex2) {
        console.debug("CCP autocreate skipped:", ex2);
      }
      setDraft({ ...makeBlankDraft(), monitoring_mode: defaultMode });
      await load();
    } catch (ex) {
      await notify({
        title: "Couldn't add equipment",
        message: formatErr(ex.response?.data?.detail) || ex.message || "Please try again.",
        variant: "err",
      });
    }
    finally { setBusy(false); }
  };

  const removeEquipment = async (eid) => {
    setBusy(true); setErr("");
    try {
      // Best-effort cleanup of CCPs tied to this equipment
      const linked = ccps.filter((c) => c.equipment_id === eid);
      for (const c of linked) {
        try { await api.delete(`/ccps/${c.id}`); } catch (e) { console.debug("ccp del skipped", e); }
      }
      await api.delete(`/equipment/${eid}`);
      await load();
    } catch (ex) {
      await notify({
        title: "Couldn't remove equipment",
        message: formatErr(ex.response?.data?.detail) || "Please try again.",
        variant: "err",
      });
    }
    finally { setBusy(false); }
  };

  // Edit modal — opens with a pre-populated copy of the equipment row;
  // saving PUTs the equipment AND its equipment-type's freq (since freq
  // lives on the type).
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);

  const openEdit = (eq) => {
    const etype = equipmentTypes.find((t) => t.id === eq.equipment_type_id);
    const ccp = ccps.find((c) => c.equipment_id === eq.id);
    setEditingId(eq.id);
    setEditDraft({
      name: eq.name || "",
      serial_number: eq.serial_number || "",
      location: eq.location || "",
      freq: Math.max(1, parseInt(etype?.collection_frequency_per_day, 10) || 1),
      // CCP fields — edited here too because they're effectively a
      // property of the equipment from the owner's mental model.
      ccp_min: ccp ? Number(ccp.min_value) : (etype?.min_value ?? 0),
      ccp_max: ccp ? Number(ccp.max_value) : (etype?.max_value ?? 0),
      ccp_unit: ccp ? (ccp.unit || "") : (etype?.parameter_unit || ""),
      ccp_parameter: ccp ? (ccp.parameter || ccp.name || "Temperature") : (etype?.parameter_name || "Temperature"),
      _ccp_id: ccp?.id,
      _etype: etype,
      _site_id: eq.site_id,
      _facility_id: eq.facility_id,
      _equipment_type_id: eq.equipment_type_id,
    });
  };

  const closeEdit = () => { setEditingId(null); setEditDraft(null); };

  const saveEdit = async () => {
    if (!editDraft) return;
    if (!editDraft.name.trim()) {
      return notify({ title: "Name required", message: "Equipment name can't be empty.", variant: "err" });
    }
    if (!editDraft.serial_number.trim()) {
      return notify({ title: "Serial required", message: "Serial / asset number can't be empty.", variant: "err" });
    }
    setBusy(true);
    try {
      // 1. PUT equipment fields
      await api.put(`/equipment/${editingId}`, {
        site_id: editDraft._site_id,
        facility_id: editDraft._facility_id,
        equipment_type_id: editDraft._equipment_type_id,
        name: editDraft.name.trim(),
        serial_number: editDraft.serial_number.trim(),
        model_number: "",
        location: editDraft.location.trim(),
      });
      // 2. If freq changed, PUT the equipment-type. Affects every piece
      //    of equipment that shares this type — same behaviour as the
      //    live Equipment page.
      const oldFreq = editDraft._etype?.collection_frequency_per_day || 1;
      if (editDraft._etype && oldFreq !== editDraft.freq) {
        await api.put(`/equipment-types/${editDraft._etype.id}`, {
          name: editDraft._etype.name,
          description: editDraft._etype.description || "",
          requires_calibration: !!editDraft._etype.requires_calibration,
          default_calibration_interval_days: editDraft._etype.default_calibration_interval_days || 365,
          calibration_interval_unit: editDraft._etype.calibration_interval_unit || "days",
          has_operating_range: !!editDraft._etype.has_operating_range,
          parameter_name: editDraft._etype.parameter_name || "",
          parameter_unit: editDraft._etype.parameter_unit || "",
          min_value: editDraft._etype.min_value ?? null,
          max_value: editDraft._etype.max_value ?? null,
          collection_frequency_per_day: editDraft.freq,
        });
      }
      // 3. If a CCP exists for this equipment and any of its threshold/unit
      //    fields changed, PUT the CCP. This is how owners adjust the
      //    acceptable range from inside the wizard — same surface as the
      //    main Equipment page.
      if (editDraft._ccp_id) {
        const original = ccps.find((c) => c.id === editDraft._ccp_id);
        const mn = Number(editDraft.ccp_min);
        const mx = Number(editDraft.ccp_max);
        if (Number.isNaN(mn) || Number.isNaN(mx)) {
          throw new Error("CCP min and max must be numbers");
        }
        if (mn >= mx) {
          throw new Error("CCP min must be less than max");
        }
        const changed =
          original.min_value !== mn ||
          original.max_value !== mx ||
          (original.unit || "") !== (editDraft.ccp_unit || "") ||
          (original.parameter || original.name || "") !== editDraft.ccp_parameter;
        if (changed) {
          await api.put(`/ccps/${editDraft._ccp_id}`, {
            equipment_id: editingId,
            name: original.name,
            parameter: editDraft.ccp_parameter,
            unit: editDraft.ccp_unit,
            min_value: mn,
            max_value: mx,
          });
        }
      }
      closeEdit();
      await load();
    } catch (ex) {
      await notify({
        title: "Couldn't save changes",
        message: formatErr(ex.response?.data?.detail) || ex.message || "Please try again.",
        variant: "err",
      });
    } finally { setBusy(false); }
  };

  const siteFacilities = facilities.filter((f) => f.site_id === activeSite);
  const visibleEquipment = equipment.filter((e) => e.facility_id === activeFacility);
  const visibleEqWithCcp = visibleEquipment.map((e) => {
    const etype = equipmentTypes.find((t) => t.id === e.equipment_type_id);
    return {
      ...e,
      _ccp: ccps.find((c) => c.equipment_id === e.id),
      _freq: Math.max(1, parseInt(etype?.collection_frequency_per_day, 10) || 1),
    };
  });

  return (
    <StepLayout
      stepIndex={stepIndex}
      totalSteps={totalSteps}
      title="Equipment & Critical Control Points"
      subtitle="Register the fridges, freezers, and hot-holding units that need monitoring."
      description={
        <div className="space-y-3">
          <p>For each piece of equipment, we'll auto-create a Critical Control Point (CCP) using the safe range you pick.</p>
          <p>Your team can then start logging readings straight away — the system flags any out-of-range value as a deviation.</p>
          <p className="text-xs opacity-70">You can fine-tune CCP ranges later from the CCPs page.</p>
        </div>
      }
      onBack={onBack}
      onNext={() => onAdvance({ ccps: true })}
      onFinishLater={onFinishLater}
      busy={busy}
      errorMsg={err}
    >
      <div className="space-y-5">
        {/* Inline tabs: most owners stay on Equipment & CCPs, but calibrated
            measurement gear (e.g. master thermometers) needs its own form
            because it doesn't map to a facility CCP. */}
        <div className="flex gap-2 border-b" style={{ borderColor: "var(--vb-border)" }}>
          <button type="button"
            className="px-3 py-2 text-sm"
            onClick={() => setTab("equipment")}
            style={{
              borderBottom: tab === "equipment" ? "2px solid var(--vb-accent)" : "2px solid transparent",
              color: tab === "equipment" ? "var(--vb-text-2)" : "var(--vb-text-1)",
              fontWeight: tab === "equipment" ? 600 : 500,
            }}
            data-testid="wiz-eq-tab-equipment">
            <Wrench size={13} className="inline -mt-0.5 mr-1.5" />
            Equipment & CCPs
          </button>
          <button type="button"
            className="px-3 py-2 text-sm"
            onClick={() => setTab("calibrated")}
            style={{
              borderBottom: tab === "calibrated" ? "2px solid var(--vb-accent)" : "2px solid transparent",
              color: tab === "calibrated" ? "var(--vb-text-2)" : "var(--vb-text-1)",
              fontWeight: tab === "calibrated" ? 600 : 500,
            }}
            data-testid="wiz-eq-tab-calibrated">
            <BadgeCheck size={13} className="inline -mt-0.5 mr-1.5" />
            Calibrated equipment
          </button>
        </div>

        {tab === "equipment" ? (
        <>
        {/* Tenant-level capture-mode default — the "welcome page" toggle.
            Owners with their own IoT temperature loggers flip this so new
            equipment defaults to exception (non-conformance) capture. */}
        <div className="vb-card p-4" data-testid="wiz-default-mode-card"
          style={{ background: "var(--vb-bg-2)", border: "1px solid var(--vb-border)" }}>
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" className="mt-1"
              checked={defaultMode === "exception"}
              onChange={(e) => setDefaultModePersist(e.target.checked ? "exception" : "scheduled")}
              data-testid="wiz-default-mode-cb" />
            <div>
              <div className="text-sm font-semibold" style={{ color: "var(--vb-text-2)" }}>
                We use our own IoT temperature data-loggers
              </div>
              <p className="text-[11px] opacity-70 m-0">
                Turn this on if a digital system already records temperatures continuously. New equipment will default to <strong>exception capture</strong> — your team only logs deviations when the logger alarms, instead of entering scheduled daily readings. You can still switch any individual unit below.
              </p>
            </div>
          </label>
        </div>
        {/* Edit equipment modal — opens when the user clicks the pencil
            icon on any row. Lets them rename, change the location, and
            tweak the readings-per-day (the latter updates the
            equipment-type record, affecting every piece of equipment of
            the same type — matches main-app behaviour). */}
        {editingId && editDraft && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(60, 40, 24, 0.6)" }}
            data-testid="wiz-eq-edit-modal">
            <div className="vb-card max-w-md w-full p-5 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-3">
                <h2 className="vb-heading text-lg m-0" style={{ color: "var(--vb-text-2)" }}>
                  Edit equipment
                </h2>
                <button onClick={closeEdit} className="opacity-60 hover:opacity-100"
                  data-testid="wiz-eq-edit-close"><XIcon size={18} /></button>
              </div>
              <div className="grid gap-3">
                <div>
                  <Label>Name *</Label>
                  <input className="vb-input" value={editDraft.name}
                    onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })}
                    data-testid="wiz-eq-edit-name" />
                </div>
                <div>
                  <Label>Serial / asset number *</Label>
                  <input className="vb-input" value={editDraft.serial_number}
                    onChange={(e) => setEditDraft({ ...editDraft, serial_number: e.target.value })}
                    data-testid="wiz-eq-edit-serial" />
                </div>
                <div>
                  <Label>Location</Label>
                  <input className="vb-input" value={editDraft.location}
                    onChange={(e) => setEditDraft({ ...editDraft, location: e.target.value })}
                    data-testid="wiz-eq-edit-location" />
                </div>
                <div>
                  <Label>Readings per day</Label>
                  <input type="number" min="1" max="24" step="1" className="vb-input"
                    value={editDraft.freq}
                    onChange={(e) => setEditDraft({ ...editDraft, freq: Math.max(1, parseInt(e.target.value || "1", 10)) })}
                    data-testid="wiz-eq-edit-freq" />
                  <p className="text-[11px] opacity-60 mt-0.5 m-0">
                    Updates the <em>{editDraft._etype?.name || "equipment type"}</em> for every piece of equipment of this type at every site.
                  </p>
                </div>
                {editDraft._ccp_id && (
                  <div className="pt-3 border-t" style={{ borderColor: "var(--vb-border)" }}>
                    <div className="text-[11px] font-semibold uppercase tracking-wider mb-2"
                      style={{ color: "var(--vb-text-2)" }}>
                      CCP — acceptable operating range
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Parameter</Label>
                        <input className="vb-input" value={editDraft.ccp_parameter}
                          onChange={(e) => setEditDraft({ ...editDraft, ccp_parameter: e.target.value })}
                          data-testid="wiz-eq-edit-ccp-param" />
                      </div>
                      <div>
                        <Label>Unit</Label>
                        <input className="vb-input" value={editDraft.ccp_unit}
                          onChange={(e) => setEditDraft({ ...editDraft, ccp_unit: e.target.value })}
                          data-testid="wiz-eq-edit-ccp-unit" />
                      </div>
                      <div>
                        <Label>Min value</Label>
                        <input type="number" step="any" className="vb-input" value={editDraft.ccp_min}
                          onChange={(e) => setEditDraft({ ...editDraft, ccp_min: e.target.value })}
                          data-testid="wiz-eq-edit-ccp-min" />
                      </div>
                      <div>
                        <Label>Max value</Label>
                        <input type="number" step="any" className="vb-input" value={editDraft.ccp_max}
                          onChange={(e) => setEditDraft({ ...editDraft, ccp_max: e.target.value })}
                          data-testid="wiz-eq-edit-ccp-max" />
                      </div>
                    </div>
                    <p className="text-[11px] opacity-60 mt-1 m-0">
                      Readings outside this range trigger a deviation in CCP — Data Capture.
                    </p>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button type="button" className="vb-btn ghost" onClick={closeEdit} disabled={busy}
                  data-testid="wiz-eq-edit-cancel">Cancel</button>
                <button type="button" className="vb-btn" onClick={saveEdit} disabled={busy}
                  data-testid="wiz-eq-edit-save">Save</button>
              </div>
            </div>
          </div>
        )}
        {/* Site + facility switchers — sized larger and explicit per user feedback. */}
        {sites.length > 1 && (
          <div>
            <Label>Site</Label>
            <div className="flex gap-2 flex-wrap" data-testid="wiz-eq-site-switcher">
              {sites.map((s) => (
                <button key={s.id} type="button"
                  className={`vb-pill ${activeSite === s.id ? "ok" : ""}`}
                  style={{
                    cursor: "pointer",
                    fontSize: 14,
                    padding: "8px 14px",
                    border: activeSite === s.id ? undefined : "1px solid var(--vb-border)",
                  }}
                  onClick={() => setActiveSite(s.id)}
                  data-testid={`wiz-eq-site-${s.id}`}>
                  {s.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {siteFacilities.length === 0 ? (
          <div className="vb-card p-6 flex items-start gap-3" data-testid="wiz-eq-no-facilities">
            <AlertCircle size={18} style={{ color: "var(--vb-warning)" }} />
            <div>
              <div className="font-semibold mb-1" style={{ color: "var(--vb-text-2)" }}>No facilities at this site</div>
              <p className="text-sm opacity-70 m-0">Go back to Step 4 and add a facility first.</p>
            </div>
          </div>
        ) : (
          <>
            <div>
              <Label>Facility</Label>
              <div className="flex gap-2 flex-wrap" data-testid="wiz-eq-fac-switcher">
                {siteFacilities.map((f) => (
                  <button key={f.id} type="button"
                    className={`vb-pill ${activeFacility === f.id ? "ok" : ""}`}
                    style={{
                      cursor: "pointer",
                      fontSize: 14,
                      padding: "8px 14px",
                      border: activeFacility === f.id ? undefined : "1px solid var(--vb-border)",
                    }}
                    onClick={() => setActiveFacility(f.id)}
                    data-testid={`wiz-eq-fac-${f.id}`}>
                    {f.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Add equipment */}
            <div className="vb-card p-6 space-y-3" data-testid="wiz-add-equipment-card">
              <div className="flex items-center gap-2">
                <Wrench size={18} style={{ color: "var(--vb-text-2)" }} />
                <h2 className="vb-heading text-xl m-0" style={{ color: "var(--vb-text-2)" }}>Add equipment</h2>
              </div>
              <div>
                <Label>Equipment type</Label>
                <select className="vb-input" value={draft.type_key}
                  onChange={(e) => setDraft({ ...draft, type_key: e.target.value })}
                  data-testid="wiz-eq-type">
                  {DEFAULT_TYPES.map((t) => (
                    <option key={t.name} value={t.name}>
                      {t.name} ({t.min}–{t.max}{t.unit})
                    </option>
                  ))}
                  <option value="__custom__">➕ Add custom equipment type…</option>
                </select>
                <p className="text-[11px] opacity-60 mt-1 m-0">
                  Pick a preset above, or use <em>Add custom</em> for niche gear (ovens, blast chillers, pH meters, etc.).
                </p>
              </div>

              {draft.type_key === "__custom__" && (
                <div className="vb-card p-3 space-y-2"
                  style={{ background: "var(--vb-bg-2)", border: "1px dashed var(--vb-border)" }}
                  data-testid="wiz-eq-custom-block">
                  <div className="text-xs opacity-70 mb-1">Define your own equipment type — name it, set the safe range, and we'll save it for future use.</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <Label>Type name *</Label>
                      <input className="vb-input" value={draft.custom.type_name}
                        onChange={(e) => setDraft({ ...draft, custom: { ...draft.custom, type_name: e.target.value } })}
                        placeholder="e.g. Blast chiller"
                        data-testid="wiz-eq-custom-name" />
                    </div>
                    <div>
                      <Label>Parameter *</Label>
                      <input className="vb-input" value={draft.custom.parameter}
                        onChange={(e) => setDraft({ ...draft, custom: { ...draft.custom, parameter: e.target.value } })}
                        placeholder="e.g. Temperature, pH, Humidity"
                        data-testid="wiz-eq-custom-parameter" />
                    </div>
                    <div>
                      <Label>Unit *</Label>
                      <input className="vb-input" value={draft.custom.unit}
                        onChange={(e) => setDraft({ ...draft, custom: { ...draft.custom, unit: e.target.value } })}
                        placeholder="e.g. °C, pH, %RH"
                        data-testid="wiz-eq-custom-unit" />
                    </div>
                    <div>
                      <Label>Readings per day</Label>
                      <input type="number" min="1" step="1" className="vb-input" value={draft.custom.freq}
                        onChange={(e) => setDraft({ ...draft, custom: { ...draft.custom, freq: Math.max(1, parseInt(e.target.value || "1", 10)) } })}
                        data-testid="wiz-eq-custom-freq" />
                      <p className="text-[11px] opacity-60 mt-0.5 m-0">How many readings staff should log per day.</p>
                    </div>
                    <div>
                      <Label>Min safe value *</Label>
                      <input type="number" step="0.1" className="vb-input" value={draft.custom.min}
                        onChange={(e) => setDraft({ ...draft, custom: { ...draft.custom, min: e.target.value } })}
                        data-testid="wiz-eq-custom-min" />
                    </div>
                    <div>
                      <Label>Max safe value *</Label>
                      <input type="number" step="0.1" className="vb-input" value={draft.custom.max}
                        onChange={(e) => setDraft({ ...draft, custom: { ...draft.custom, max: e.target.value } })}
                        data-testid="wiz-eq-custom-max" />
                    </div>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Equipment name / label *</Label>
                  <input className="vb-input" value={draft.name}
                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    placeholder="e.g. Walk-in Chiller #1"
                    data-testid="wiz-eq-name" />
                </div>
                <div>
                  <Label>Serial / asset number *</Label>
                  <input className="vb-input" value={draft.serial_number}
                    onChange={(e) => setDraft({ ...draft, serial_number: e.target.value })}
                    placeholder="e.g. CHL-001 or factory serial"
                    data-testid="wiz-eq-serial" />
                </div>
                <div className="sm:col-span-2">
                  <Label>Location inside facility</Label>
                  <input className="vb-input" value={draft.location}
                    onChange={(e) => setDraft({ ...draft, location: e.target.value })}
                    placeholder="e.g. Back wall, next to prep station"
                    data-testid="wiz-eq-location" />
                </div>
                <div className="sm:col-span-2 rounded p-2" style={{ border: "1px solid var(--vb-border)", background: "var(--vb-bg-2)" }}>
                  <Label>Monitoring mode</Label>
                  <label className="flex items-start gap-2 mb-1.5 cursor-pointer">
                    <input type="radio" name="wiz-eq-mode" className="mt-0.5"
                      checked={(draft.monitoring_mode || "scheduled") === "scheduled"}
                      onChange={() => setDraft({ ...draft, monitoring_mode: "scheduled" })}
                      data-testid="wiz-eq-mode-scheduled" />
                    <span className="text-[12px]"><strong>Scheduled</strong> — staff log readings each day.</span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="radio" name="wiz-eq-mode" className="mt-0.5"
                      checked={draft.monitoring_mode === "exception"}
                      onChange={() => setDraft({ ...draft, monitoring_mode: "exception" })}
                      data-testid="wiz-eq-mode-exception" />
                    <span className="text-[12px]"><strong>Exception</strong> — external logger monitors continuously; staff log deviations only.</span>
                  </label>
                </div>
                {/* Readings per day (preset path) — hidden for custom because
                    the custom block already has its own freq input. Drives the
                    number of CCP capture slots per day on the live page. */}
                {draft.type_key !== "__custom__" && draft.monitoring_mode !== "exception" && (
                  <div className="sm:col-span-2">
                    <Label>Readings per day</Label>
                    <input type="number" min="1" max="24" step="1" className="vb-input"
                      value={draft.freq}
                      onChange={(e) => setDraft({ ...draft, freq: Math.max(1, parseInt(e.target.value || "1", 10)) })}
                      data-testid="wiz-eq-freq" />
                    <p className="text-[11px] opacity-60 mt-0.5 m-0">
                      How many CCP readings staff capture each day for this type.
                      Drives the per-day reading slots on the CCP — Data Capture page.
                    </p>
                  </div>
                )}
                <div className="sm:col-span-2">
                  <label className="flex items-start gap-2 cursor-pointer rounded p-2"
                    style={{ border: "1px solid var(--vb-border)", background: "var(--vb-bg-2)" }}
                    data-testid="wiz-eq-cal-toggle">
                    <input type="checkbox"
                      className="mt-0.5"
                      checked={!!draft.requires_calibration}
                      onChange={(e) => setDraft({ ...draft, requires_calibration: e.target.checked })}
                      data-testid="wiz-eq-cal-cb" />
                    <div>
                      <div className="text-sm font-semibold" style={{ color: "var(--vb-text-2)" }}>
                        Requires calibration
                      </div>
                      <p className="text-[11px] opacity-70 m-0">
                        Turn on for sensors / probes that need periodic accuracy checks. The Equipment page will then surface a calibration due-date and reminders.
                      </p>
                    </div>
                  </label>
                </div>
              </div>
              <div className="flex justify-end pt-2">
                <button type="button" className="vb-btn" onClick={addEquipment} disabled={busy}
                  data-testid="wiz-eq-add-btn">
                  <Plus size={14} /> Add equipment & CCP
                </button>
              </div>
            </div>

            {/* List */}
            {visibleEqWithCcp.length === 0 ? (
              <div className="vb-card p-6 text-center text-sm opacity-70" data-testid="wiz-eq-empty">
                No equipment yet for this facility.
              </div>
            ) : (
              <div className="vb-card p-0 overflow-hidden" data-testid="wiz-eq-list">
                <table className="vb-table">
                  <thead><tr><th>Equipment</th><th>Serial</th><th>CCP range</th><th>Readings/day</th><th></th></tr></thead>
                  <tbody>
                    {visibleEqWithCcp.map((e) => (
                      <tr key={e.id} data-testid={`wiz-eq-row-${e.id}`}>
                        <td>
                          <strong>{e.name}</strong>
                          {e.location && <div className="text-xs opacity-60">{e.location}</div>}
                        </td>
                        <td className="text-xs font-mono">{e.serial_number}</td>
                        <td className="text-xs">
                          {e._ccp ? (
                            <span className="vb-pill ok text-[10px]">
                              <Gauge size={9} /> {e._ccp.min_value}–{e._ccp.max_value}{e._ccp.unit}
                            </span>
                          ) : (
                            <span className="opacity-50">No CCP</span>
                          )}
                        </td>
                        <td className="text-xs">
                          <span className="vb-pill text-[10px]" data-testid={`wiz-eq-freq-${e.id}`}
                            style={{ background: "var(--vb-bg-2)", border: "1px solid var(--vb-border)" }}>
                            {e.monitoring_mode === "exception" ? "N/A" : `${e._freq}× / day`}
                          </span>
                        </td>
                        <td className="text-right whitespace-nowrap">
                          <button type="button" className="vb-btn ghost sm mr-1"
                            onClick={() => openEdit(e)}
                            data-testid={`wiz-eq-edit-${e.id}`}>
                            <Pencil size={11} />
                          </button>
                          <button type="button" className="vb-btn ghost sm"
                            onClick={() => removeEquipment(e.id)}
                            style={{ color: "var(--vb-error)" }}
                            data-testid={`wiz-eq-del-${e.id}`}>
                            <Trash2 size={11} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
        </>
        ) : (
          /* ---------- Calibrated equipment tab ---------- */
          <CalibratedTab
            sites={sites}
            facilities={facilities}
            calibrated={calibrated}
            activeSite={activeSite}
            setActiveSite={setActiveSite}
            calDraft={calDraft}
            setCalDraft={setCalDraft}
            resetCalDraft={() => setCalDraft(makeBlankCalDraft())}
            onReload={load}
            busy={busy}
            setBusy={setBusy}
            notify={notify}
          />
        )}
      </div>
    </StepLayout>
  );
}

/**
 * Calibrated-equipment tab — for measurement gear that's calibrated against
 * a standard (master thermometers, weight check tools, pH meters, etc).
 * Backed by the same `/calibrated-equipment` endpoint used on the customer
 * Equipment page, so anything added here shows up under "Calibrated
 * Equipment" tab on the live app.
 */
function CalibratedTab({
  sites, facilities, calibrated, activeSite, setActiveSite,
  calDraft, setCalDraft, resetCalDraft, onReload, busy, setBusy, notify,
}) {
  const siteFacilities = facilities.filter((f) => f.site_id === activeSite);
  const siteCalibrated = calibrated.filter((c) => c.site_id === activeSite);

  const addCalibrated = async () => {
    if (!activeSite) {
      return notify({ title: "Pick a site", message: "Select a site first.", variant: "err" });
    }
    if (!calDraft.facility_id) {
      return notify({ title: "Pick a facility", message: "Select a facility first.", variant: "err" });
    }
    if (!calDraft.name?.trim()) {
      return notify({ title: "Name required", message: "Give this calibrated equipment a name.", variant: "err" });
    }
    if (!calDraft.serial_number?.trim()) {
      return notify({ title: "Serial required", message: "Serial number is mandatory — it identifies this unit on calibration certs.", variant: "err" });
    }
    if (!calDraft.last_calibrated_at) {
      return notify({ title: "Last calibrated date required", message: "When was this last calibrated?", variant: "err" });
    }
    setBusy(true);
    try {
      await api.post("/calibrated-equipment", {
        site_id: activeSite,
        facility_id: calDraft.facility_id,
        name: calDraft.name.trim(),
        serial_number: calDraft.serial_number.trim(),
        model_number: (calDraft.model_number || "").trim(),
        last_calibrated_at: new Date(calDraft.last_calibrated_at).toISOString(),
        last_calibrated_by: calDraft.last_calibrated_by || "",
        calibration_interval_value: Number(calDraft.calibration_interval_value) || 12,
        calibration_interval_unit: calDraft.calibration_interval_unit || "months",
        calibration_certificate_url: calDraft.calibration_certificate_url || null,
      });
      resetCalDraft();
      await onReload();
    } catch (ex) {
      await notify({
        title: "Couldn't add calibrated equipment",
        message: formatErr(ex.response?.data?.detail) || "Please try again.",
        variant: "err",
      });
    } finally { setBusy(false); }
  };

  const removeCal = async (cid) => {
    setBusy(true);
    try { await api.delete(`/calibrated-equipment/${cid}`); await onReload(); }
    catch (ex) {
      await notify({
        title: "Couldn't remove",
        message: formatErr(ex.response?.data?.detail) || "Please try again.",
        variant: "err",
      });
    } finally { setBusy(false); }
  };

  return (
    <>
      {sites.length > 1 && (
        <div>
          <Label>Site</Label>
          <div className="flex gap-2 flex-wrap" data-testid="wiz-cal-site-switcher">
            {sites.map((s) => (
              <button key={s.id} type="button"
                className={`vb-pill ${activeSite === s.id ? "ok" : ""}`}
                style={{
                  cursor: "pointer", fontSize: 14, padding: "8px 14px",
                  border: activeSite === s.id ? undefined : "1px solid var(--vb-border)",
                }}
                onClick={() => setActiveSite(s.id)}
                data-testid={`wiz-cal-site-${s.id}`}>
                {s.name}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="vb-card p-6 space-y-3" data-testid="wiz-add-calibrated-card">
        <h2 className="vb-heading text-xl m-0" style={{ color: "var(--vb-text-2)" }}>
          Add calibrated equipment
        </h2>
        <p className="text-xs opacity-70 -mt-1.5">
          For measurement gear that's calibrated to a reference standard (e.g. master thermometer, test weight, pH meter). Tracking the last-calibrated date here drives reminder banners on the Equipment page.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Facility *</Label>
            <select className="vb-input" value={calDraft.facility_id}
              onChange={(e) => setCalDraft({ ...calDraft, facility_id: e.target.value })}
              disabled={siteFacilities.length === 0}
              data-testid="wiz-cal-facility">
              <option value="">{siteFacilities.length === 0 ? "No facilities — add one in Step 4" : "Select facility"}</option>
              {siteFacilities.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </div>
          <div>
            <Label>Name *</Label>
            <input className="vb-input" value={calDraft.name}
              onChange={(e) => setCalDraft({ ...calDraft, name: e.target.value })}
              placeholder="e.g. Calibration Thermometer"
              data-testid="wiz-cal-name" />
          </div>
          <div>
            <Label>Serial number *</Label>
            <input className="vb-input" value={calDraft.serial_number}
              onChange={(e) => setCalDraft({ ...calDraft, serial_number: e.target.value })}
              placeholder="Serial number"
              data-testid="wiz-cal-serial" />
          </div>
          <div>
            <Label>Model number (optional)</Label>
            <input className="vb-input" value={calDraft.model_number}
              onChange={(e) => setCalDraft({ ...calDraft, model_number: e.target.value })}
              placeholder="Model number"
              data-testid="wiz-cal-model" />
          </div>
          <div>
            <Label>Last calibrated date *</Label>
            <input type="date" className="vb-input"
              value={calDraft.last_calibrated_at}
              max={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setCalDraft({ ...calDraft, last_calibrated_at: e.target.value })}
              data-testid="wiz-cal-last-date" />
          </div>
          <div>
            <Label>Calibrated by (optional)</Label>
            <input className="vb-input" value={calDraft.last_calibrated_by}
              onChange={(e) => setCalDraft({ ...calDraft, last_calibrated_by: e.target.value })}
              placeholder="e.g. ABC Calibration Lab"
              data-testid="wiz-cal-by" />
          </div>
          <div className="sm:col-span-2">
            <Label>Calibration interval *</Label>
            <div className="grid grid-cols-[1fr_140px] gap-2">
              <input type="number" min="1" className="vb-input"
                value={calDraft.calibration_interval_value}
                onChange={(e) => setCalDraft({ ...calDraft, calibration_interval_value: e.target.value })}
                data-testid="wiz-cal-interval-value" />
              <select className="vb-input"
                value={calDraft.calibration_interval_unit}
                onChange={(e) => setCalDraft({ ...calDraft, calibration_interval_unit: e.target.value })}
                data-testid="wiz-cal-interval-unit">
                <option value="months">Months</option>
                <option value="days">Days</option>
              </select>
            </div>
          </div>
          <div className="sm:col-span-2">
            <Label>Calibration certificate URL (optional)</Label>
            <input className="vb-input" value={calDraft.calibration_certificate_url}
              onChange={(e) => setCalDraft({ ...calDraft, calibration_certificate_url: e.target.value })}
              placeholder="https://..."
              data-testid="wiz-cal-cert-url" />
          </div>
        </div>
        <div className="flex justify-end pt-2">
          <button type="button" className="vb-btn" onClick={addCalibrated} disabled={busy}
            data-testid="wiz-cal-add-btn">
            <Plus size={14} /> Save
          </button>
        </div>
      </div>

      {siteCalibrated.length === 0 ? (
        <div className="vb-card p-6 text-center text-sm opacity-70" data-testid="wiz-cal-empty">
          No calibrated equipment yet for this site.
        </div>
      ) : (
        <div className="vb-card p-0 overflow-hidden" data-testid="wiz-cal-list">
          <table className="vb-table">
            <thead><tr><th>Name</th><th>Serial</th><th>Last calibrated</th><th>Interval</th><th></th></tr></thead>
            <tbody>
              {siteCalibrated.map((c) => (
                <tr key={c.id} data-testid={`wiz-cal-row-${c.id}`}>
                  <td><strong>{c.name}</strong>{c.model_number && <div className="text-xs opacity-60">{c.model_number}</div>}</td>
                  <td className="text-xs font-mono">{c.serial_number}</td>
                  <td className="text-xs">{c.last_calibrated_at ? c.last_calibrated_at.slice(0, 10) : "—"}</td>
                  <td className="text-xs">{c.calibration_interval_value} {c.calibration_interval_unit}</td>
                  <td className="text-right">
                    <button type="button" className="vb-btn ghost sm"
                      onClick={() => removeCal(c.id)}
                      style={{ color: "var(--vb-error)" }}
                      data-testid={`wiz-cal-del-${c.id}`}>
                      <Trash2 size={11} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function Label({ children }) {
  return (
    <label className="block text-[11px] font-semibold mb-1 uppercase tracking-wider" style={{ color: "var(--vb-text-2)" }}>
      {children}
    </label>
  );
}
